export const GRAPHICS_COURSES = [
  {
    id: 'game-development-unity',
    title: 'Game Development with Unity',
    description: 'Create 2D and 3D games using Unity engine with C# scripting and advanced game mechanics.',
    level: 'intermediate',
    totalDuration: '15-18 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['C# programming basics', '3D mathematics understanding', 'Basic game design concepts'],
    learningOutcomes: [
      'Master Unity editor and development workflow',
      'Implement game mechanics and physics',
      'Create interactive UI and game systems',
      'Build multiplayer and networking features',
      'Optimize game performance',
      'Deploy games to multiple platforms'
    ],
    modules: [
      {
        id: 'unity-fundamentals',
        title: 'Unity Engine Fundamentals',
        description: 'Core Unity concepts, scene management, and component-based architecture',
        duration: '120 minutes',
        difficulty: 'beginner',
        objectives: [
          'Navigate Unity editor and interface',
          'Understand GameObject and Component system',
          'Create scenes and manage assets',
          'Implement basic C# scripting'
        ],
        content: `# Unity Engine Fundamentals

## Unity Editor Overview

### Key Windows and Panels:
- **Scene View**: 3D workspace for positioning objects
- **Game View**: Preview of the final game
- **Hierarchy**: Tree view of GameObjects in scene
- **Project**: Asset management and file browser
- **Inspector**: Properties and components panel
- **Console**: Debug messages and errors

### Project Structure:
\`\`\`
Assets/
├── Scenes/          # Game scenes (.unity files)
├── Scripts/         # C# scripts
├── Materials/       # Visual materials
├── Textures/        # Image assets
├── Models/          # 3D models
├── Audio/           # Sound effects and music
├── Prefabs/         # Reusable game objects
└── Animations/      # Animation clips and controllers
\`\`\`

## GameObject and Component System

### Basic GameObject Creation
\`\`\`csharp
using UnityEngine;

public class GameObjectManager : MonoBehaviour
{
    // Create GameObjects at runtime
    void Start()
    {
        // Create empty GameObject
        GameObject newObject = new GameObject("MyObject");
        
        // Create primitive objects
        GameObject cube = GameObject.CreatePrimitive(PrimitiveType.Cube);
        GameObject sphere = GameObject.CreatePrimitive(PrimitiveType.Sphere);
        
        // Position objects
        cube.transform.position = new Vector3(0, 0, 0);
        sphere.transform.position = new Vector3(2, 0, 0);
        
        // Add components
        Rigidbody rb = cube.AddComponent<Rigidbody>();
        rb.mass = 2.0f;
        
        // Find existing GameObjects
        GameObject player = GameObject.FindWithTag("Player");
        GameObject[] enemies = GameObject.FindGameObjectsWithTag("Enemy");
    }
}
\`\`\`

### Component Communication
\`\`\`csharp
// Player Controller Script
using UnityEngine;

public class PlayerController : MonoBehaviour
{
    [Header("Movement Settings")]
    public float moveSpeed = 5.0f;
    public float jumpForce = 10.0f;
    
    [Header("Components")]
    private Rigidbody rb;
    private Animator animator;
    private AudioSource audioSource;
    
    [Header("Ground Check")]
    public Transform groundCheck;
    public LayerMask groundMask;
    private bool isGrounded;
    
    void Start()
    {
        // Get required components
        rb = GetComponent<Rigidbody>();
        animator = GetComponent<Animator>();
        audioSource = GetComponent<AudioSource>();
        
        // Validate components
        if (rb == null)
            Debug.LogError("Rigidbody component missing!");
    }
    
    void Update()
    {
        HandleInput();
        CheckGrounded();
        UpdateAnimations();
    }
    
    void HandleInput()
    {
        // Movement input
        float horizontal = Input.GetAxis("Horizontal");
        float vertical = Input.GetAxis("Vertical");
        
        Vector3 movement = new Vector3(horizontal, 0, vertical) * moveSpeed;
        rb.velocity = new Vector3(movement.x, rb.velocity.y, movement.z);
        
        // Jump input
        if (Input.GetButtonDown("Jump") && isGrounded)
        {
            Jump();
        }
    }
    
    void Jump()
    {
        rb.AddForce(Vector3.up * jumpForce, ForceMode.Impulse);
        animator.SetTrigger("Jump");
        audioSource.PlayOneShot(jumpSound);
    }
    
    void CheckGrounded()
    {
        isGrounded = Physics.CheckSphere(groundCheck.position, 0.1f, groundMask);
        animator.SetBool("IsGrounded", isGrounded);
    }
    
    void UpdateAnimations()
    {
        float speed = rb.velocity.magnitude;
        animator.SetFloat("Speed", speed);
    }
}
\`\`\`

## Scene Management and Game Flow

### Scene Loading and Transitions
\`\`\`csharp
using UnityEngine;
using UnityEngine.SceneManagement;
using System.Collections;

public class SceneManager : MonoBehaviour
{
    [Header("UI References")]
    public GameObject loadingScreen;
    public UnityEngine.UI.Slider loadingBar;
    public UnityEngine.UI.Text loadingText;
    
    [Header("Transition Settings")]
    public float transitionTime = 1.0f;
    
    public void LoadScene(string sceneName)
    {
        StartCoroutine(LoadSceneAsync(sceneName));
    }
    
    public void LoadScene(int sceneIndex)
    {
        StartCoroutine(LoadSceneAsync(sceneIndex));
    }
    
    IEnumerator LoadSceneAsync(string sceneName)
    {
        // Show loading screen
        loadingScreen.SetActive(true);
        
        // Start loading scene
        AsyncOperation operation = SceneManager.LoadSceneAsync(sceneName);
        operation.allowSceneActivation = false;
        
        while (!operation.isDone)
        {
            // Update loading progress
            float progress = Mathf.Clamp01(operation.progress / 0.9f);
            loadingBar.value = progress;
            loadingText.text = \$"Loading... {progress * 100:F0}%";
            
            // Check if loading is almost complete
            if (operation.progress >= 0.9f)
            {
                loadingText.text = "Press any key to continue";
                
                if (Input.anyKeyDown)
                {
                    operation.allowSceneActivation = true;
                }
            }
            
            yield return null;
        }
    }
    
    public void QuitGame()
    {
        #if UNITY_EDITOR
            UnityEditor.EditorApplication.isPlaying = false;
        #else
            Application.Quit();
        #endif
    }
    
    public void RestartLevel()
    {
        SceneManager.LoadScene(SceneManager.GetActiveScene().buildIndex);
    }
}
\`\`\`

### Game State Management
\`\`\`csharp
using UnityEngine;
using System;

public enum GameState
{
    MainMenu,
    Playing,
    Paused,
    GameOver,
    Victory
}

public class GameManager : MonoBehaviour
{
    public static GameManager Instance { get; private set; }
    
    [Header("Game Settings")]
    public GameState currentState = GameState.MainMenu;
    public int playerLives = 3;
    public int score = 0;
    public float gameTime = 0f;
    
    [Header("UI Panels")]
    public GameObject mainMenuPanel;
    public GameObject gameplayUI;
    public GameObject pausePanel;
    public GameObject gameOverPanel;
    public GameObject victoryPanel;
    
    // Events
    public static event Action<GameState> OnGameStateChanged;
    public static event Action<int> OnScoreChanged;
    public static event Action<int> OnLivesChanged;
    
    void Awake()
    {
        // Singleton pattern
        if (Instance == null)
        {
            Instance = this;
            DontDestroyOnLoad(gameObject);
        }
        else
        {
            Destroy(gameObject);
        }
    }
    
    void Start()
    {
        ChangeGameState(GameState.MainMenu);
    }
    
    void Update()
    {
        if (currentState == GameState.Playing)
        {
            gameTime += Time.deltaTime;
            
            // Pause game
            if (Input.GetKeyDown(KeyCode.Escape))
            {
                ChangeGameState(GameState.Paused);
            }
        }
        else if (currentState == GameState.Paused)
        {
            if (Input.GetKeyDown(KeyCode.Escape))
            {
                ChangeGameState(GameState.Playing);
            }
        }
    }
    
    public void ChangeGameState(GameState newState)
    {
        if (currentState == newState) return;
        
        currentState = newState;
        OnGameStateChanged?.Invoke(currentState);
        
        // Handle state-specific logic
        switch (currentState)
        {
            case GameState.MainMenu:
                Time.timeScale = 1f;
                ShowPanel(mainMenuPanel);
                break;
                
            case GameState.Playing:
                Time.timeScale = 1f;
                ShowPanel(gameplayUI);
                break;
                
            case GameState.Paused:
                Time.timeScale = 0f;
                ShowPanel(pausePanel);
                break;
                
            case GameState.GameOver:
                Time.timeScale = 0f;
                ShowPanel(gameOverPanel);
                break;
                
            case GameState.Victory:
                Time.timeScale = 0f;
                ShowPanel(victoryPanel);
                break;
        }
    }
    
    void ShowPanel(GameObject panelToShow)
    {
        // Hide all panels
        mainMenuPanel.SetActive(false);
        gameplayUI.SetActive(false);
        pausePanel.SetActive(false);
        gameOverPanel.SetActive(false);
        victoryPanel.SetActive(false);
        
        // Show target panel
        if (panelToShow != null)
            panelToShow.SetActive(true);
    }
    
    public void AddScore(int points)
    {
        score += points;
        OnScoreChanged?.Invoke(score);
    }
    
    public void LoseLife()
    {
        playerLives--;
        OnLivesChanged?.Invoke(playerLives);
        
        if (playerLives <= 0)
        {
            ChangeGameState(GameState.GameOver);
        }
    }
    
    public void StartGame()
    {
        ResetGame();
        ChangeGameState(GameState.Playing);
    }
    
    void ResetGame()
    {
        score = 0;
        playerLives = 3;
        gameTime = 0f;
        
        OnScoreChanged?.Invoke(score);
        OnLivesChanged?.Invoke(playerLives);
    }
}
\`\`\`

## Physics and Collision Detection

### Physics-Based Movement
\`\`\`csharp
using UnityEngine;

public class PhysicsController : MonoBehaviour
{
    [Header("Movement")]
    public float moveForce = 10f;
    public float maxSpeed = 5f;
    public float jumpForce = 15f;
    
    [Header("Physics")]
    public float groundDrag = 5f;
    public float airDrag = 1f;
    
    private Rigidbody rb;
    private bool isGrounded;
    
    void Start()
    {
        rb = GetComponent<Rigidbody>();
        rb.freezeRotation = true; // Prevent tipping over
    }
    
    void Update()
    {
        // Ground check
        isGrounded = Physics.Raycast(transform.position, Vector3.down, 1.1f);
        
        // Handle drag
        rb.drag = isGrounded ? groundDrag : airDrag;
        
        // Movement input
        Vector3 moveDirection = GetMoveDirection();
        MovePlayer(moveDirection);
        
        // Speed limit
        LimitSpeed();
        
        // Jump
        if (Input.GetKeyDown(KeyCode.Space) && isGrounded)
        {
            Jump();
        }
    }
    
    Vector3 GetMoveDirection()
    {
        float horizontalInput = Input.GetAxisRaw("Horizontal");
        float verticalInput = Input.GetAxisRaw("Vertical");
        
        return transform.forward * verticalInput + transform.right * horizontalInput;
    }
    
    void MovePlayer(Vector3 moveDirection)
    {
        // Ground movement
        if (isGrounded)
        {
            rb.AddForce(moveDirection.normalized * moveForce * 10f, ForceMode.Force);
        }
        // Air movement (reduced control)
        else
        {
            rb.AddForce(moveDirection.normalized * moveForce * 2f, ForceMode.Force);
        }
    }
    
    void LimitSpeed()
    {
        Vector3 flatVelocity = new Vector3(rb.velocity.x, 0f, rb.velocity.z);
        
        if (flatVelocity.magnitude > maxSpeed)
        {
            Vector3 limitedVelocity = flatVelocity.normalized * maxSpeed;
            rb.velocity = new Vector3(limitedVelocity.x, rb.velocity.y, limitedVelocity.z);
        }
    }
    
    void Jump()
    {
        // Reset y velocity before jumping
        rb.velocity = new Vector3(rb.velocity.x, 0f, rb.velocity.z);
        rb.AddForce(transform.up * jumpForce, ForceMode.Impulse);
    }
    
    void OnCollisionEnter(Collision collision)
    {
        if (collision.gameObject.CompareTag("Enemy"))
        {
            // Handle enemy collision
            TakeDamage(10);
        }
        else if (collision.gameObject.CompareTag("Collectible"))
        {
            // Handle collectible
            CollectItem(collision.gameObject);
        }
    }
    
    void TakeDamage(int damage)
    {
        Debug.Log(\$"Player took {damage} damage!");
        GameManager.Instance.LoseLife();
    }
    
    void CollectItem(GameObject item)
    {
        GameManager.Instance.AddScore(100);
        Destroy(item);
    }
}
\`\`\``,
        practice: {
          task: 'Create a complete 3D platformer game with character movement, enemies, collectibles, and multiple levels',
          starter: `// Build a 3D platformer that includes:
// 1. Character controller with jumping and running
// 2. Enemy AI with patrol and chase behavior
// 3. Collectible system with power-ups
// 4. Level progression and checkpoints
// 5. UI system with health, score, and menus

using UnityEngine;

public class PlatformerPlayer : MonoBehaviour
{
    [Header("Movement Settings")]
    public float moveSpeed = 5f;
    public float jumpHeight = 2f;
    
    // Add your implementation here
    
    void Start()
    {
        // Initialize player
    }
    
    void Update()
    {
        // Handle input and movement
    }
}

public class EnemyAI : MonoBehaviour
{
    // Add enemy behavior implementation
}

public class LevelManager : MonoBehaviour
{
    // Add level management implementation
}`,
          hints: [
            'Implement character controller with proper physics',
            'Create enemy AI with state machines',
            'Add collectible spawning and management system',
            'Implement checkpoint and respawn mechanics',
            'Create smooth camera following system',
            'Add particle effects and audio feedback'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'unity-1',
              question: 'What is the main advantage of Unity\'s component-based architecture?',
              options: [
                'Better graphics performance',
                'Modular and reusable game object functionality',
                'Faster compilation times',
                'Easier networking implementation'
              ],
              correctAnswer: 1,
              difficulty: 'medium' as const,
              explanation: 'Component-based architecture allows for modular, reusable functionality that can be mixed and matched on GameObjects.',
              points: 15
            }
          ],
          passThreshold: 75
        }
      }
    ],
    type: 'graphics'
  },
  {
    id: 'computer-graphics-opengl',
    title: 'Computer Graphics with OpenGL',
    description: 'Master 3D graphics programming with OpenGL, shaders, and advanced rendering techniques.',
    level: 'advanced',
    totalDuration: '12-15 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['C++ programming', 'Linear algebra', 'Understanding of 3D mathematics'],
    learningOutcomes: [
      'Master OpenGL rendering pipeline',
      'Write vertex and fragment shaders',
      'Implement advanced lighting models',
      'Create texture mapping and materials',
      'Build 3D graphics applications',
      'Optimize rendering performance'
    ],
    modules: [
      {
        id: 'opengl-rendering',
        title: 'OpenGL Rendering Pipeline',
        description: 'Core OpenGL concepts, shader programming, and modern rendering techniques',
        duration: '150 minutes',
        difficulty: 'advanced',
        objectives: [
          'Understand OpenGL rendering pipeline',
          'Write GLSL shaders',
          'Implement 3D transformations',
          'Create advanced lighting effects'
        ],
        content: `# OpenGL Rendering Pipeline

## OpenGL Fundamentals

### Setting up OpenGL Context
\`\`\`cpp
// OpenGL setup with GLFW and GLAD
#include <GL/glew.h>
#include <GLFW/glfw3.h>
#include <iostream>

class OpenGLRenderer {
private:
    GLFWwindow* window;
    int width, height;
    
public:
    bool Initialize(int w, int h, const char* title) {
        width = w;
        height = h;
        
        // Initialize GLFW
        if (!glfwInit()) {
            std::cerr << "Failed to initialize GLFW" << std::endl;
            return false;
        }
        
        // Set OpenGL version and profile
        glfwWindowHint(GLFW_CONTEXT_VERSION_MAJOR, 3);
        glfwWindowHint(GLFW_CONTEXT_VERSION_MINOR, 3);
        glfwWindowHint(GLFW_OPENGL_PROFILE, GLFW_OPENGL_CORE_PROFILE);
        
        // Create window
        window = glfwCreateWindow(width, height, title, nullptr, nullptr);
        if (!window) {
            std::cerr << "Failed to create GLFW window" << std::endl;
            glfwTerminate();
            return false;
        }
        
        glfwMakeContextCurrent(window);
        
        // Initialize GLEW
        if (glewInit() != GLEW_OK) {
            std::cerr << "Failed to initialize GLEW" << std::endl;
            return false;
        }
        
        // Set viewport
        glViewport(0, 0, width, height);
        
        // Enable depth testing
        glEnable(GL_DEPTH_TEST);
        
        return true;
    }
    
    void MainLoop() {
        while (!glfwWindowShouldClose(window)) {
            // Handle input
            ProcessInput();
            
            // Clear screen
            glClearColor(0.2f, 0.3f, 0.3f, 1.0f);
            glClear(GL_COLOR_BUFFER_BIT | GL_DEPTH_BUFFER_BIT);
            
            // Render scene
            Render();
            
            // Swap buffers and poll events
            glfwSwapBuffers(window);
            glfwPollEvents();
        }
        
        Cleanup();
    }
    
private:
    void ProcessInput() {
        if (glfwGetKey(window, GLFW_KEY_ESCAPE) == GLFW_PRESS) {
            glfwSetWindowShouldClose(window, true);
        }
    }
    
    void Render() {
        // Rendering code here
    }
    
    void Cleanup() {
        glfwDestroyWindow(window);
        glfwTerminate();
    }
};
\`\`\`

## Shader Programming

### Vertex Shader
\`\`\`glsl
// vertex_shader.glsl
#version 330 core

layout (location = 0) in vec3 aPos;
layout (location = 1) in vec3 aNormal;
layout (location = 2) in vec2 aTexCoord;

uniform mat4 model;
uniform mat4 view;
uniform mat4 projection;
uniform mat3 normalMatrix;

out vec3 FragPos;
out vec3 Normal;
out vec2 TexCoord;

void main() {
    // Transform vertex position to world space
    FragPos = vec3(model * vec4(aPos, 1.0));
    
    // Transform normal to world space
    Normal = normalMatrix * aNormal;
    
    // Pass texture coordinates
    TexCoord = aTexCoord;
    
    // Final position in clip space
    gl_Position = projection * view * vec4(FragPos, 1.0);
}
\`\`\`

### Fragment Shader with Phong Lighting
\`\`\`glsl
// fragment_shader.glsl
#version 330 core

struct Material {
    sampler2D diffuse;
    sampler2D specular;
    float shininess;
};

struct DirectionalLight {
    vec3 direction;
    vec3 ambient;
    vec3 diffuse;
    vec3 specular;
};

struct PointLight {
    vec3 position;
    vec3 ambient;
    vec3 diffuse;
    vec3 specular;
    
    float constant;
    float linear;
    float quadratic;
};

struct SpotLight {
    vec3 position;
    vec3 direction;
    float cutOff;
    float outerCutOff;
    
    vec3 ambient;
    vec3 diffuse;
    vec3 specular;
    
    float constant;
    float linear;
    float quadratic;
};

in vec3 FragPos;
in vec3 Normal;
in vec2 TexCoord;

uniform vec3 viewPos;
uniform Material material;
uniform DirectionalLight dirLight;
uniform PointLight pointLights[4];
uniform SpotLight spotLight;

out vec4 FragColor;

// Function prototypes
vec3 CalcDirLight(DirectionalLight light, vec3 normal, vec3 viewDir);
vec3 CalcPointLight(PointLight light, vec3 normal, vec3 fragPos, vec3 viewDir);
vec3 CalcSpotLight(SpotLight light, vec3 normal, vec3 fragPos, vec3 viewDir);

void main() {
    vec3 norm = normalize(Normal);
    vec3 viewDir = normalize(viewPos - FragPos);
    
    // Directional light
    vec3 result = CalcDirLight(dirLight, norm, viewDir);
    
    // Point lights
    for(int i = 0; i < 4; i++) {
        result += CalcPointLight(pointLights[i], norm, FragPos, viewDir);
    }
    
    // Spot light
    result += CalcSpotLight(spotLight, norm, FragPos, viewDir);
    
    FragColor = vec4(result, 1.0);
}

vec3 CalcDirLight(DirectionalLight light, vec3 normal, vec3 viewDir) {
    vec3 lightDir = normalize(-light.direction);
    
    // Diffuse shading
    float diff = max(dot(normal, lightDir), 0.0);
    
    // Specular shading
    vec3 reflectDir = reflect(-lightDir, normal);
    float spec = pow(max(dot(viewDir, reflectDir), 0.0), material.shininess);
    
    // Combine results
    vec3 ambient = light.ambient * vec3(texture(material.diffuse, TexCoord));
    vec3 diffuse = light.diffuse * diff * vec3(texture(material.diffuse, TexCoord));
    vec3 specular = light.specular * spec * vec3(texture(material.specular, TexCoord));
    
    return ambient + diffuse + specular;
}

vec3 CalcPointLight(PointLight light, vec3 normal, vec3 fragPos, vec3 viewDir) {
    vec3 lightDir = normalize(light.position - fragPos);
    
    // Diffuse shading
    float diff = max(dot(normal, lightDir), 0.0);
    
    // Specular shading
    vec3 reflectDir = reflect(-lightDir, normal);
    float spec = pow(max(dot(viewDir, reflectDir), 0.0), material.shininess);
    
    // Attenuation
    float distance = length(light.position - fragPos);
    float attenuation = 1.0 / (light.constant + light.linear * distance + 
                              light.quadratic * (distance * distance));
    
    // Combine results
    vec3 ambient = light.ambient * vec3(texture(material.diffuse, TexCoord));
    vec3 diffuse = light.diffuse * diff * vec3(texture(material.diffuse, TexCoord));
    vec3 specular = light.specular * spec * vec3(texture(material.specular, TexCoord));
    
    ambient *= attenuation;
    diffuse *= attenuation;
    specular *= attenuation;
    
    return ambient + diffuse + specular;
}
\`\`\`

### Shader Program Management
\`\`\`cpp
// Shader.h
#include <GL/glew.h>
#include <string>
#include <fstream>
#include <sstream>
#include <iostream>
#include <glm/glm.hpp>
#include <glm/gtc/type_ptr.hpp>

class Shader {
public:
    unsigned int ID;
    
    Shader(const char* vertexPath, const char* fragmentPath) {
        // Read shader source files
        std::string vertexCode;
        std::string fragmentCode;
        std::ifstream vShaderFile;
        std::ifstream fShaderFile;
        
        vShaderFile.exceptions(std::ifstream::failbit | std::ifstream::badbit);
        fShaderFile.exceptions(std::ifstream::failbit | std::ifstream::badbit);
        
        try {
            vShaderFile.open(vertexPath);
            fShaderFile.open(fragmentPath);
            std::stringstream vShaderStream, fShaderStream;
            
            vShaderStream << vShaderFile.rdbuf();
            fShaderStream << fShaderFile.rdbuf();
            
            vShaderFile.close();
            fShaderFile.close();
            
            vertexCode = vShaderStream.str();
            fragmentCode = fShaderStream.str();
        }
        catch (std::ifstream::failure& e) {
            std::cout << "ERROR::SHADER::FILE_NOT_SUCCESSFULLY_READ" << std::endl;
        }
        
        const char* vShaderCode = vertexCode.c_str();
        const char* fShaderCode = fragmentCode.c_str();
        
        // Compile shaders
        unsigned int vertex, fragment;
        
        // Vertex shader
        vertex = glCreateShader(GL_VERTEX_SHADER);
        glShaderSource(vertex, 1, &vShaderCode, NULL);
        glCompileShader(vertex);
        CheckCompileErrors(vertex, "VERTEX");
        
        // Fragment shader
        fragment = glCreateShader(GL_FRAGMENT_SHADER);
        glShaderSource(fragment, 1, &fShaderCode, NULL);
        glCompileShader(fragment);
        CheckCompileErrors(fragment, "FRAGMENT");
        
        // Shader program
        ID = glCreateProgram();
        glAttachShader(ID, vertex);
        glAttachShader(ID, fragment);
        glLinkProgram(ID);
        CheckCompileErrors(ID, "PROGRAM");
        
        // Clean up
        glDeleteShader(vertex);
        glDeleteShader(fragment);
    }
    
    void Use() {
        glUseProgram(ID);
    }
    
    // Utility uniform functions
    void SetBool(const std::string &name, bool value) const {
        glUniform1i(glGetUniformLocation(ID, name.c_str()), (int)value);
    }
    
    void SetInt(const std::string &name, int value) const {
        glUniform1i(glGetUniformLocation(ID, name.c_str()), value);
    }
    
    void SetFloat(const std::string &name, float value) const {
        glUniform1f(glGetUniformLocation(ID, name.c_str()), value);
    }
    
    void SetVec3(const std::string &name, const glm::vec3 &value) const {
        glUniform3fv(glGetUniformLocation(ID, name.c_str()), 1, &value[0]);
    }
    
    void SetMat4(const std::string &name, const glm::mat4 &mat) const {
        glUniformMatrix4fv(glGetUniformLocation(ID, name.c_str()), 1, GL_FALSE, &mat[0][0]);
    }
    
private:
    void CheckCompileErrors(unsigned int shader, std::string type) {
        int success;
        char infoLog[1024];
        
        if (type != "PROGRAM") {
            glGetShaderiv(shader, GL_COMPILE_STATUS, &success);
            if (!success) {
                glGetShaderInfoLog(shader, 1024, NULL, infoLog);
                std::cout << "ERROR::SHADER_COMPILATION_ERROR of type: " << type << "\\n" 
                         << infoLog << std::endl;
            }
        } else {
            glGetProgramiv(shader, GL_LINK_STATUS, &success);
            if (!success) {
                glGetProgramInfoLog(shader, 1024, NULL, infoLog);
                std::cout << "ERROR::PROGRAM_LINKING_ERROR of type: " << type << "\\n" 
                         << infoLog << std::endl;
            }
        }
    }
};
\`\`\`

## Advanced Rendering Techniques

### Shadow Mapping
\`\`\`cpp
// Shadow mapping implementation
class ShadowRenderer {
private:
    unsigned int depthMapFBO;
    unsigned int depthMap;
    const unsigned int SHADOW_WIDTH = 1024, SHADOW_HEIGHT = 1024;
    
public:
    void InitializeShadowMapping() {
        // Generate framebuffer for depth map
        glGenFramebuffers(1, &depthMapFBO);
        
        // Generate depth texture
        glGenTextures(1, &depthMap);
        glBindTexture(GL_TEXTURE_2D, depthMap);
        glTexImage2D(GL_TEXTURE_2D, 0, GL_DEPTH_COMPONENT, 
                     SHADOW_WIDTH, SHADOW_HEIGHT, 0, 
                     GL_DEPTH_COMPONENT, GL_FLOAT, NULL);
        glTexParameteri(GL_TEXTURE_2D, GL_TEXTURE_MIN_FILTER, GL_NEAREST);
        glTexParameteri(GL_TEXTURE_2D, GL_TEXTURE_MAG_FILTER, GL_NEAREST);
        glTexParameteri(GL_TEXTURE_2D, GL_TEXTURE_WRAP_S, GL_CLAMP_TO_BORDER);
        glTexParameteri(GL_TEXTURE_2D, GL_TEXTURE_WRAP_T, GL_CLAMP_TO_BORDER);
        float borderColor[] = { 1.0f, 1.0f, 1.0f, 1.0f };
        glTexParameterfv(GL_TEXTURE_2D, GL_TEXTURE_BORDER_COLOR, borderColor);
        
        // Attach depth texture as FBO's depth buffer
        glBindFramebuffer(GL_FRAMEBUFFER, depthMapFBO);
        glFramebufferTexture2D(GL_FRAMEBUFFER, GL_DEPTH_ATTACHMENT, 
                              GL_TEXTURE_2D, depthMap, 0);
        glDrawBuffer(GL_NONE);
        glReadBuffer(GL_NONE);
        glBindFramebuffer(GL_FRAMEBUFFER, 0);
    }
    
    void RenderDepthMap(Shader& depthShader, const glm::mat4& lightSpaceMatrix) {
        // Configure viewport for shadow map
        glViewport(0, 0, SHADOW_WIDTH, SHADOW_HEIGHT);
        glBindFramebuffer(GL_FRAMEBUFFER, depthMapFBO);
        glClear(GL_DEPTH_BUFFER_BIT);
        
        // Configure shader and matrices
        depthShader.Use();
        depthShader.SetMat4("lightSpaceMatrix", lightSpaceMatrix);
        
        // Render scene from light's point of view
        RenderScene(depthShader);
        
        glBindFramebuffer(GL_FRAMEBUFFER, 0);
    }
    
    void RenderSceneWithShadows(Shader& shader, const glm::mat4& lightSpaceMatrix) {
        // Reset viewport
        glViewport(0, 0, screenWidth, screenHeight);
        glClear(GL_COLOR_BUFFER_BIT | GL_DEPTH_BUFFER_BIT);
        
        // Configure shader
        shader.Use();
        shader.SetMat4("lightSpaceMatrix", lightSpaceMatrix);
        shader.SetInt("shadowMap", 1);
        
        // Bind shadow map
        glActiveTexture(GL_TEXTURE1);
        glBindTexture(GL_TEXTURE_2D, depthMap);
        
        // Render scene
        RenderScene(shader);
    }
};
\`\`\``,
        practice: {
          task: 'Build a complete 3D graphics engine with advanced lighting, shadows, and post-processing effects',
          starter: `// Create a graphics engine that includes:
// 1. Deferred rendering pipeline
// 2. Multiple light types with shadows
// 3. Post-processing effects (bloom, SSAO, tone mapping)
// 4. Material system with PBR shading
// 5. Model loading and animation

#include <GL/glew.h>
#include <GLFW/glfw3.h>
#include <glm/glm.hpp>

class GraphicsEngine {
private:
    // Add your implementation
    
public:
    bool Initialize(int width, int height);
    void Render();
    void Shutdown();
    
    // Add your methods here
};

// Implement deferred rendering
class DeferredRenderer {
    // Add deferred rendering implementation
};

// Implement post-processing pipeline
class PostProcessor {
    // Add post-processing effects
};`,
          hints: [
            'Implement G-buffer for deferred rendering',
            'Create cascaded shadow maps for directional lights',
            'Add screen-space ambient occlusion (SSAO)',
            'Implement physically-based rendering (PBR)',
            'Create bloom and tone mapping post-effects',
            'Add model loading with Assimp library'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'opengl-1',
              question: 'What is the primary purpose of the vertex shader in the OpenGL pipeline?',
              options: [
                'To calculate pixel colors',
                'To transform vertex positions and pass data to the fragment shader',
                'To handle texture sampling',
                'To manage lighting calculations'
              ],
              correctAnswer: 1,
              difficulty: 'medium' as const,
              explanation: 'The vertex shader transforms vertex positions from object space to clip space and passes data to the fragment shader.',
              points: 15
            }
          ],
          passThreshold: 75
        }
      }
    ],
    type: 'graphics'
  }
];